import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const areProIdSetsEqual = (currentSet, nextProIdList) => {
  if (currentSet.size !== nextProIdList.length) {
    return false;
  }
  return nextProIdList.every((proId) => currentSet.has(proId));
};

// Create Context
const DynamicRoutingValidationContext = createContext();

// Provider Component
export const DynamicRoutingValidationProvider = ({ children }) => {
  const [shipmentUpdates, setShipmentUpdates] = useState({
    incorrectPse: new Set(),
    lateShipments: new Set()
  });

  const [excludedPros, setExcludedPros] = useState(new Set());
  const [selectedProsForCityRoute, setSelectedProsForCityRoute] = useState(new Set());

  // Function to add a proId to a specific category
  const addShipmentUpdate = useCallback((proIdList, section) => {
    setShipmentUpdates((prevState) => ({
      ...prevState,
      [section]: new Set([...prevState[section], ...proIdList])
    }));
  }, []);

  // Function to toggle pro (Add or Remove)
  const toggleExcludedPro = useCallback((proId, isInclude) => {
    setExcludedPros((prevSet) => {
      const newSet = new Set(prevSet);
      isInclude ? newSet.delete(proId) : newSet.add(proId);
      return newSet;
    });
  }, []);

  const initializeProsForCityRoute = useCallback((proIdList) => {
    setSelectedProsForCityRoute((prevSet) => {
      if (areProIdSetsEqual(prevSet, proIdList)) {
        return prevSet;
      }
      return new Set(proIdList);
    });
  }, []);

  const toggleProsForCityRoute = useCallback((proId, isSelected) => {
    setSelectedProsForCityRoute((prevSet) => {
      const isAlreadySelected = prevSet.has(proId);
      if ((isSelected && isAlreadySelected) || (!isSelected && !isAlreadySelected)) {
        return prevSet;
      }
      const newSet = new Set(prevSet);
      if (isSelected) {
        newSet.add(proId);
      } else {
        newSet.delete(proId);
      }
      return newSet;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      shipmentUpdates,
      addShipmentUpdate,
      excludedPros,
      toggleExcludedPro,
      selectedProsForCityRoute,
      initializeProsForCityRoute,
      toggleProsForCityRoute
    }),
    [
      shipmentUpdates,
      addShipmentUpdate,
      excludedPros,
      toggleExcludedPro,
      selectedProsForCityRoute,
      initializeProsForCityRoute,
      toggleProsForCityRoute
    ]
  );

  return (
    <DynamicRoutingValidationContext.Provider value={contextValue}>
      {children}
    </DynamicRoutingValidationContext.Provider>
  );
};

// Custom Hook for easy access
export const useDynamicRoutingValidationContext = () => useContext(DynamicRoutingValidationContext);
